/**
 * 底層 HTTP 封裝（基於原生 fetch）
 *
 * 設計目標：
 * - 不引入額外依賴（沿用 authService.ts 的原生 fetch 風格）
 * - baseURL / timeout / 統一 header 注入
 * - Request interceptor: 自動帶 Authorization、Language
 * - Response interceptor: 拆業務 statusCode、401 觸發「重新登入」流程
 *   （GET / HEAD 續期成功後自動重發，呼叫端無感；非冪等方法只觸發續期不重發）
 * - 支援 per-request errorHandler，回傳 true 表示「已自行處理」
 * - 型別友善：每個 method 都可指定回傳資料的型別
 *
 * 使用方式：
 *   import { useHttp } from '@/api/http'
 *   const balance = await useHttp.get<BalanceResult>('/v1/wallets/balance')
 *
 * 注意：
 * - 請勿在 module top-level 直接呼叫 useAuthStore() / i18n.global.locale，
 *   必須在 request() 執行時才取，否則 Pinia/i18n 還沒掛載會炸。
 * - 401 **不再**直接 invalidate（見下方 auth recovery hook）；呼叫端仍會拿到 reject。
 */
import { useAuthStore } from '@/stores/auth'
import { i18n } from '@/i18n'
import { API_BASE } from '@/runtimeConfig'

// ============================================================================
// Base URL — 全專案唯一一條，從 runtimeConfig（API_BASE）讀
// ============================================================================

// 🛑 **不要**在這裡寫 `const BASE_URL = API_BASE`：API_BASE 是 live binding，
//    要等 main.ts 的 initRuntimeConfig() 之後才有值；module scope 快照會凍在空字串。
//    直接在送出請求時（request() 內）讀 API_BASE 即可。

// ============================================================================
// Auth recovery hook — 401 → 重新 login / ticketLogin
// ============================================================================

/**
 * 「401 之後試著重新登入」的處理器。由 **App.vue 在 setup 期註冊**
 * （實作 = `useTokenRetry().refreshToken`：最多 3 次、間隔 800ms、期間顯示重試 toast）。
 *
 * 回傳 `true` = 已換到新 token 並存回 authStore；`false` = 救不回來（呼叫端／
 * onTerminalFail 會走 authError）。
 *
 * **未註冊時（回傳 false）** 是刻意的設計：replay 模式（ResultDetailApp）用的是
 * URL 上的一次性 token，沒有可續期的憑證，401 就該直接把錯誤丟回給畫面，不重試。
 */
export type AuthRecoveryHandler = () => Promise<boolean>

let authRecoveryHandler: AuthRecoveryHandler | null = null

/** 註冊 / 解除 401 重新登入處理器（App.vue setup 註冊、unmount 解除）。 */
export const setAuthRecoveryHandler = (handler: AuthRecoveryHandler | null): void => {
  authRecoveryHandler = handler
}

/**
 * 觸發重新登入流程。**single-flight 由 handler 自己保證**（useTokenRetry 內以
 * in-flight promise 共用），所以併發 401 或「攔截器已觸發、bootstrap 想再 await 一次」
 * 都只會實際重登一輪。未註冊 handler 時直接回 false。
 */
export const runAuthRecovery = async (): Promise<boolean> => {
  if (!authRecoveryHandler) return false
  try {
    return await authRecoveryHandler()
  } catch {
    return false
  }
}

// ============================================================================
// Types
// ============================================================================

/**
 * 後端統一回傳結構
 * - 相容 `statusCode`（共享專案風格）與 `code`（本專案 backend 風格）
 */
export interface CommonResponse<T = unknown> {
  code?: number
  statusCode?: number | string
  msg?: string
  message?: string
  msgCode?: number
  data?: T
  datetime?: string
}

/**
 * 自訂錯誤處理器
 * - 回傳 true：表示已處理（呼叫端拿到的 reject 仍會發生，但不會再被全域 toast 等接手）
 * - 回傳 false：表示未處理（保留給未來全域處理擴充）
 */
export type ErrorHandler = (
  statusCode: number,
  msg: string,
  msgCode?: number,
) => boolean | Promise<boolean>

/**
 * Request 設定
 */
export interface RequestConfig extends Omit<RequestInit, 'body' | 'method'> {
  /** 覆寫 baseURL；預設用 getBackendApiUrl() */
  baseURL?: string
  /** Query string 參數（GET 用最多，POST 也可附加） */
  params?: Record<string, unknown>
  /** Request body；非字串會自動 JSON.stringify */
  body?: unknown
  /** 逾時毫秒數，預設 30000 */
  timeout?: number
  /** Per-request 自訂錯誤處理 */
  errorHandler?: ErrorHandler
  /** 不自動注入 Authorization */
  skipAuth?: boolean
  /** 直接拿原始 envelope（不自動拆 .data） */
  raw?: boolean
  /**
   * 401 → 重新登入成功後，**自動用新 token 重發這個請求**（呼叫端完全無感）。
   *
   * 預設值 = 「這個 method 是否冪等」：GET / HEAD 預設 `true`，其餘預設 `false`。
   * 只有在你確定該端點重複執行是安全的（或後端有做冪等保護）時，才對非 GET 明確設 `true`；
   * 例如 `POST /spin`、`POST /buy` 這種會扣款 / 產生局號的，**永遠不要開**。
   *
   * ⚠️ `logout` 也不該開：401 後先重新登入、再重發 logout，會把剛換到的新 session 登出。
   */
  retryOn401?: boolean
  /**
   * 本請求**屬於重新登入流程本身**（例如續期成功後順手重抓餘額）→ 401 時
   * 既不觸發也不等待續期。
   *
   * ⚠️ 沒有這個旗標會**自我死鎖**：`refreshAuth()` 內的 fetchBalance 若 401，
   * 冪等重發會去 `await runAuthRecovery()`，而那個 promise 正在等 `refreshAuth()` 回來。
   * 語意上也對 —— 才剛換到的新 token 就被打回 401，再續期一次也沒有意義。
   */
  skipAuthRecovery?: boolean
}

/**
 * 統一錯誤型別；所有失敗（HTTP / 業務 / timeout / 網路）都會 throw 這個
 */
export class ApiError extends Error {
  /** HTTP status，業務錯誤時通常是 200，網路錯誤是 0 */
  status: number
  /** 業務 statusCode/code；HTTP 錯誤時 = HTTP status；逾時/網路錯誤時 = -1 */
  statusCode: number
  /** 訊息（給使用者看） */
  msg: string
  /** 業務 msgCode，預設 0 */
  msgCode: number
  /** 原始 data（業務錯誤可能附帶） */
  data: unknown

  constructor(opts: {
    status: number
    statusCode: number
    msg: string
    msgCode?: number
    data?: unknown
  }) {
    super(opts.msg)
    this.name = 'ApiError'
    this.status = opts.status
    this.statusCode = opts.statusCode
    this.msg = opts.msg
    this.msgCode = opts.msgCode ?? 0
    this.data = opts.data
  }
}

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * 預設逾時 8 秒（2026-07 從 30s 調降）。
 *
 * 這是手遊 host —— 進站的 login / balance / bets 都擋在 loading 畫面前面，
 * 30 秒代表後端沒回應時玩家要乾等半分鐘才看到錯誤。8 秒足夠涵蓋正常的行動網路
 * 往返，又能讓失敗快點浮出來。
 *
 * 個別端點要更長，用 per-request `timeout`（見 RequestConfig）覆寫，不要改這個常數。
 */
const DEFAULT_TIMEOUT = 8000

const buildQuery = (params?: Record<string, unknown>): string => {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      value.forEach((v) => search.append(key, String(v)))
    } else {
      search.append(key, String(value))
    }
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

const joinUrl = (base: string, path: string): string => {
  if (/^https?:\/\//i.test(path)) return path
  if (!base) return path
  const b = base.endsWith('/') ? base.slice(0, -1) : base
  const p = path.startsWith('/') ? path : `/${path}`
  return b + p
}

const injectAuthHeader = (headers: Headers, skipAuth: boolean | undefined): void => {
  if (skipAuth) return
  try {
    const authStore = useAuthStore()
    const token = authStore.token
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', token)
    }
  } catch {
    // Pinia 尚未初始化（極少見），靜默跳過
  }
}

const injectLanguageHeader = (headers: Headers): void => {
  // 用 `Accept-Language` 而不是自訂的 `Language` — 前者是 CORS-safelisted header
  // 不會觸發 preflight，後者會逼後端把它加進 Access-Control-Allow-Headers，
  // 任何新加的端點 / 服務都要重設 CORS 才能用。
  // 值是 i18n primary subtag (`zh` / `en`)，是合法的 Accept-Language 內容。
  if (headers.has('Accept-Language')) return
  try {
    headers.set('Accept-Language', String(i18n.global.locale.value))
  } catch {
    // i18n 尚未初始化，靜默跳過
  }
}

const serializeBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined || body === null) return undefined
  if (typeof body === 'string') return body
  if (body instanceof FormData) return body
  if (body instanceof Blob) return body
  if (body instanceof ArrayBuffer) return body
  if (body instanceof URLSearchParams) return body
  return JSON.stringify(body)
}

const parsePayload = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('Content-Type') || ''
  if (contentType.includes('application/json')) {
    return response.json()
  }
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

const readBizCode = (payload: unknown): number => {
  if (!payload || typeof payload !== 'object') return 0
  const env = payload as CommonResponse
  if (env.statusCode !== undefined && env.statusCode !== null) {
    const n = Number(env.statusCode)
    return Number.isFinite(n) ? n : 0
  }
  if (env.code !== undefined && env.code !== null) {
    const n = Number(env.code)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

// ============================================================================
// Core request
// ============================================================================

/**
 * 401 續期後可安全「自動重發」的 method（冪等）。
 * 非冪等的（POST / PUT / DELETE …）預設不重發，要重發得逐支 `retryOn401: true` 明示。
 */
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD'])

const request = async <T = unknown>(
  url: string,
  method: string,
  config: RequestConfig = {},
): Promise<T> => {
  const {
    baseURL,
    params,
    body,
    timeout = DEFAULT_TIMEOUT,
    headers: extraHeaders,
    errorHandler,
    skipAuth,
    raw,
    retryOn401,
    skipAuthRecovery,
    ...rest
  } = config

  // ---- Request interceptor ----
  // API_BASE 於呼叫當下才讀（live binding，見 runtimeConfig.ts）
  const finalUrl = joinUrl(baseURL ?? API_BASE, url) + buildQuery(params)
  const serialized = serializeBody(body)

  /**
   * 送出一次請求。
   *
   * ⚠️ headers **每次都重新組**，不能把 Headers 物件提到外面共用 ——
   * `Authorization` 是送出前才從 authStore 注入的，401 重發時必須帶**新 token**；
   * 沿用舊 Headers 等於拿已失效的 token 再打一次，然後又 401（而且很難查）。
   * timeout 的 AbortController 同理，每次都要新的（舊的已經 abort 過就永遠是 aborted）。
   */
  const send = async (): Promise<Response> => {
    const headers = new Headers(extraHeaders ?? {})

    // JSON content-type 預設（FormData / 字串 body 不要帶，瀏覽器會自帶 boundary）
    const isPlainJson =
      serialized !== undefined && typeof serialized === 'string' && !(body instanceof FormData)
    if (isPlainJson && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    injectAuthHeader(headers, skipAuth)
    injectLanguageHeader(headers)

    const ac = new AbortController()
    const timer = setTimeout(() => ac.abort(), timeout)
    try {
      return await fetch(finalUrl, {
        ...rest,
        method,
        headers,
        body: serialized,
        signal: ac.signal,
      })
    } catch (err) {
      const e = err as Error & { name?: string }
      if (e?.name === 'AbortError') {
        throw new ApiError({
          status: 0,
          statusCode: -1,
          msg: 'Request timed out',
        })
      }
      throw new ApiError({
        status: 0,
        statusCode: -1,
        msg: e?.message || 'Network error',
      })
    } finally {
      clearTimeout(timer)
    }
  }

  let response = await send()

  // ---- Response interceptor ----

  // 401 → 觸發「重新 login / ticketLogin」流程（見上方 auth recovery hook）。
  //
  //   - `skipAuth` 的端點（/v1/users/{dev/login,login/{ticket},guest/login}）自己回 401
  //     = 初次登入就換不到 token → 交給 bootstrap 設 authError。**這裡不能觸發重新登入**，
  //     否則登入失敗會遞迴呼叫登入。
  //   - 冪等請求（GET / HEAD，或明確 `retryOn401: true`）→ **await 續期，成功就用新 token
  //     自動重發一次**，呼叫端完全無感（拿到的是重發後的正常結果）。只重發一次，
  //     第二次再 401 就放棄，不會遞迴。
  //   - 非冪等請求（POST/PUT/DELETE，預設）→ 只 fire-and-forget 觸發續期，**不重發**，
  //     呼叫端照樣拿到 reject。避免下注 / 扣款類端點被送兩次。
  //   - `skipAuthRecovery`（續期流程自己發的請求）→ 完全不碰，避免自我死鎖。
  //   - 未註冊 handler（replay 模式）→ runAuthRecovery 直接回 false，等同單純 reject。
  if (response.status === 401 && !skipAuth && !skipAuthRecovery) {
    const replayable = retryOn401 ?? IDEMPOTENT_METHODS.has(method.toUpperCase())
    if (replayable) {
      if (await runAuthRecovery()) response = await send()
    } else {
      void runAuthRecovery()
    }
  }

  // 續期後重發仍 401（或本來就不重發）→ 交給呼叫端
  if (response.status === 401) {
    throw new ApiError({
      status: 401,
      statusCode: 401,
      msg: 'Unauthorized',
    })
  }

  // 其他 HTTP 錯誤
  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      statusCode: response.status,
      msg: `HTTP ${response.status}`,
    })
  }

  const payload = await parsePayload(response)

  // 非 envelope 結構（例如直接回 string / array）→ 直接給回去
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return payload as T
  }

  const env = payload as CommonResponse<T>
  const bizCode = readBizCode(env)

  // 業務錯誤
  if (bizCode !== 0) {
    const msg = env.msg ?? env.message ?? `Business error ${bizCode}`
    const msgCode = Number(env.msgCode ?? 0)

    if (errorHandler) {
      try {
        await errorHandler(bizCode, String(msg), msgCode)
      } catch {
        // handler 自身爆炸不影響原本的 reject
      }
    }

    throw new ApiError({
      status: response.status,
      statusCode: bizCode,
      msg: String(msg),
      msgCode,
      data: env.data,
    })
  }

  // 成功：raw=true 拿原始 envelope，否則只回 .data
  if (raw) return env as unknown as T
  return env.data as T
}

// ============================================================================
// Public API
// ============================================================================

/**
 * 全專案 API 入口。**新建 api 一律走這支**（共用 envelope `CommonResponse<T>`，預設自動拆 `.data`）。
 * `useHttp.get<T>` 的 `T` = `data` 的內容（不是整個 envelope）；慣例見 `src/api/CLAUDE.md`。
 */
export const useHttp = {
  get: <T = unknown>(
    url: string,
    params?: Record<string, unknown>,
    config?: Omit<RequestConfig, 'params' | 'body'>,
  ) => request<T>(url, 'GET', { ...config, params }),

  post: <T = unknown>(url: string, body?: unknown, config?: Omit<RequestConfig, 'body'>) =>
    request<T>(url, 'POST', { ...config, body }),

  put: <T = unknown>(url: string, body?: unknown, config?: Omit<RequestConfig, 'body'>) =>
    request<T>(url, 'PUT', { ...config, body }),

  patch: <T = unknown>(url: string, body?: unknown, config?: Omit<RequestConfig, 'body'>) =>
    request<T>(url, 'PATCH', { ...config, body }),

  delete: <T = unknown>(url: string, body?: unknown, config?: Omit<RequestConfig, 'body'>) =>
    request<T>(url, 'DELETE', { ...config, body }),
}

/**
 * 直接 export 出來給需要完整控制的場景（罕見）
 */
export const httpRequest = request
